import type { Observable } from 'rxjs';
import {
  takeUntil,
  share,
  mergeMap,
  distinct,
  filter,
  bufferCount,
  merge,
  map,
  shareReplay,
} from 'rxjs';

import type {
  DestinationStaticInfo,
  ProductCheapestAvailabilityResponse,
  DestinationAvailabilityCriteria,
  HotelIngredientAdapter,
  HotelConstraintQL,
} from '@libs/trip-planner-interfaces';

import type { SmartSearchService } from '../search/smartSearchService';
import { isHotelIngredientAdapter } from '../recipe/ingredients/typeGuards';

export class DestinationSearchResults {
  public readonly availability: Observable<
    ProductCheapestAvailabilityResponse[]
  >;
  public readonly staticInfo: Observable<Record<string, DestinationStaticInfo>>;
  private readonly extraStaticInfo: Observable<
    Record<string, DestinationStaticInfo>
  >;

  private readonly startingStaticInfo: Observable<
    Record<string, DestinationStaticInfo>
  >;

  constructor(
    searchService: SmartSearchService,
    criteria: DestinationAvailabilityCriteria,
    cancel: Observable<unknown>
  ) {
    const destinationHotelIngredient =
      criteria.userCriteria.recipe.ingredients?.[
        criteria.userCriteria.ingredientIndex
      ];

    if (
      !destinationHotelIngredient ||
      !isHotelIngredientAdapter(destinationHotelIngredient)
    ) {
      throw new Error('Destination Hotel Ingredient not found in recipe');
    }

    this.availability = searchService
      .searchDestinationAvailability(criteria)
      .pipe(takeUntil(cancel), shareReplay());

    this.startingStaticInfo = searchService
      .searchDestinationStaticInfo(destinationHotelIngredient)
      .pipe(takeUntil(cancel), share());
    const missingHotelCodes = this.startingStaticInfo.pipe(
      map((destinationStaticInfoDictionary) =>
        Object.values(destinationStaticInfoDictionary).map(
          (destinationStaticInfo) => destinationStaticInfo.hotelCode
        )
      ),
      mergeMap((existingCodes) => {
        const index = new Set(existingCodes);

        return this.availability.pipe(
          mergeMap((responseByChunk) =>
            responseByChunk.map(
              (availableProduct) => availableProduct.placeCode
            )
          ),
          distinct(),
          filter((hotelCodes) => !index.has(hotelCodes))
        );
      }),
      share()
    );

    const bufferCountSize = 25;

    this.extraStaticInfo = missingHotelCodes.pipe(
      bufferCount(bufferCountSize),
      mergeMap((missingHotels) => {
        const newStaticInfoConstraint: HotelConstraintQL = {
          selectedHotelTypes: [],
          boardBasis: [],
          hotelCodes: missingHotels,
          maxNumberOfHotels: missingHotels.length,
        };
        const ingredient: HotelIngredientAdapter = {
          ...destinationHotelIngredient,
          constraint: newStaticInfoConstraint,
        };
        return searchService.searchDestinationStaticInfo(ingredient);
      }),
      takeUntil(cancel)
    );

    this.staticInfo = merge(this.startingStaticInfo, this.extraStaticInfo);
  }
}
